import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { TagComponent } from '../tag/tag.component';
import { TextComponent } from '../text/text.component';
import { TagInfo } from '../../interfaces/reserve-loading.model';

/** tagColor 對應的標籤樣式(與原本方案標籤顯示邏輯一致，未定義的顏色不顯示) */
const TAG_COLOR_STYLE_MAP: Record<
  string,
  { tagColor: 'outline-red' | 'outline-purple2' | 'outline-blue2'; textColor: string }
> = {
  '1': { tagColor: 'outline-red', textColor: 'red-600' },
  '2': { tagColor: 'outline-purple2', textColor: 'purple-700' },
  '3': { tagColor: 'outline-blue2', textColor: 'blue-700' },
};

/**
 * 方案標籤清單：超過指定行數時自動收合，多出的標籤以「+N」徽章表示，點擊後展開全部
 */
@Component({
  selector: 'app-tag-list',
  standalone: true,
  imports: [TagComponent, TextComponent],
  templateUrl: './tag-list.component.html',
  styleUrl: './tag-list.component.scss',
})
export class TagListComponent implements AfterViewInit, OnDestroy {
  tags = input<TagInfo[]>([]);
  maxRows = input<number>(3);

  @ViewChild('container') containerRef!: ElementRef<HTMLElement>;

  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);
  private resizeObserver?: ResizeObserver;

  visibleCount = signal(0);
  measured = signal(false);
  expanded = signal(false);

  renderedTags = computed(() =>
    this.tags().filter(tag => !!TAG_COLOR_STYLE_MAP[tag.tagColor])
  );
  hiddenCount = computed(() => this.renderedTags().length - this.visibleCount());

  tagStyle(tagColor: string) {
    return TAG_COLOR_STYLE_MAP[tagColor];
  }

  ngAfterViewInit(): void {
    // ngAfterViewInit 仍在 Angular 這輪變更偵測的呼叫堆疊內，此時若同步呼叫
    // detectChanges() 量測版面，容易觸發 ExpressionChangedAfterItHasBeenCheckedError
    // 導致量測中途被中斷、visibleCount 卡在錯誤的中間值。改用 afterNextRender
    // 延後到本輪渲染完全結束、跳出 Angular 呼叫堆疊後才量測，就不會有這個衝突。
    afterNextRender(
      () => {
        this.measure();
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.containerRef.nativeElement);
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  toggleExpand(): void {
    const next = !this.expanded();
    this.expanded.set(next);
    if (!next) {
      this.measure();
    }
  }

  // 量測目前寬度下每個標籤實際換行的位置，算出前 maxRows 行能容納幾個標籤
  private measure(): void {
    if (this.expanded()) return;

    const container = this.containerRef?.nativeElement;
    const total = this.renderedTags().length;
    if (!container || total === 0) {
      this.visibleCount.set(total);
      this.measured.set(true);
      return;
    }

    // 先全部顯示，才能正確量測每個標籤實際換行後的位置
    this.measured.set(false);
    this.visibleCount.set(total);
    this.cdr.detectChanges();

    const items = Array.from(
      container.querySelectorAll<HTMLElement>('[data-tag-item]')
    );
    const rowTops = [...new Set(items.map(item => item.offsetTop))].sort(
      (a, b) => a - b
    );
    const maxRows = this.maxRows();

    if (rowTops.length <= maxRows) {
      this.measured.set(true);
      return;
    }

    const boundaryTop = rowTops[maxRows - 1];
    let count = items.filter(item => item.offsetTop <= boundaryTop).length;

    // 換行位置已量測完成，讓 visibleCount 開始真正控制顯示數量，
    // 否則下面收縮迴圈裡 template 仍會因為 !measured() 而顯示全部標籤
    this.measured.set(true);

    // 加入「+N」徽章後確認是否仍在最後一行內，放不下就少顯示一個標籤讓出空間
    while (count > 0) {
      this.visibleCount.set(count);
      this.cdr.detectChanges();
      const badge = container.querySelector<HTMLElement>('[data-more-badge]');
      if (!badge || badge.offsetTop <= boundaryTop) break;
      count--;
    }

    this.visibleCount.set(count);
  }
}
